import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { Autocomplete, Box, CircularProgress, Grid, Paper, Popper, Typography, useTheme } from '@mui/material'
import { styled, SxProps } from '@mui/material/styles'
import { useEntitiesFromSearch } from 'api/search'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import ProductItemList from 'components/ProductRows/ProductList'
import React, { FC, forwardRef, useMemo, useState } from 'react'
import { OrganizationTableItem } from 'types/organizations_table'
import RenderInput from './RenderInput'
import './style.css'
import TypePopperWide from './TypePopperWide'

const StyledPopper = styled(Popper)(({ theme }) => ({
  width: '100% !important',
  minWidth: 300,
  maxWidth: 545,
  zIndex: theme.zIndex.modal + 1,
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  boxShadow: theme.shadows[6],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}))

const ListboxBase = forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(function ListboxBase(props, ref) {
  return <ul {...props} ref={ref} />
})
const StyledListbox = styled(ListboxBase)(({ theme }) => ({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  background: theme.palette.background.paper,
}))

const colorFor = (category?: string) => {
  switch (category) {
    case 'Digital':
    case 'Social':
    case 'SMM':
      return 'rgb(88, 188, 128)'
    case 'Creative':
      return 'rgb(157, 139, 241)'
    case 'LaunchPad':
      return 'rgb(250, 192, 30)'
    case 'Website':
    case 'Website Build':
    case 'Display':
    case 'Graphic Design':
    case 'Hosting':
    case 'Videography':
    case 'Photography':
    case 'Audio':
    case 'Video':
      return 'rgba(213, 184, 255, 1)'
    case 'Search':
    case 'Management Fee':
    case 'Management Fee/Retainer':
    case 'Retainer':
    case 'Setup Fee':
      return 'rgb(119, 255, 173)'
    case 'Organic':
      return 'rgb(25, 181, 254)'
    default:
      return 'rgb(133, 142, 150)'
  }
}

const extractDealInfo = (deals: any[], deal_uuid?: string) => {
  const d = deals?.find(d => d?.deal_uuid === deal_uuid) ?? null
  return { dealStatus: d?.dealStatus ?? '', name: d?.name ?? '' }
}

interface Props {
  products: OrganizationTableItem[]
  value: OrganizationTableItem | null
  onChange: (newValue: OrganizationTableItem | null) => void
  label?: string
  error?: boolean
  isLoading?: boolean
  disabled?: boolean
  sx?: SxProps
  placeholder?: string
  /** collapsed vs expanded grid sizes (like GlobalUserSelect) */
  xsCollapsed?: number
  xsExpanded?: number
}

const TaskProductAutocomplete: FC<Props> = ({
  products = [],
  value,
  onChange,
  label,
  isLoading,
  disabled,
  sx,
  placeholder,
  xsCollapsed = 5,
  xsExpanded = 24,
}) => {
  const [open, setOpen] = useState(false)
  const { deals } = useEntitiesFromSearch()
  const theme = useTheme()
  // sort: deal_uuid → subcategory → title
  const sorted = useMemo(() => {
    const s = (v: unknown) => (v ?? '') as string
    return [...products].sort((a, b) => {
      const d = s(a.deal_uuid).localeCompare(s(b.deal_uuid))
      if (d !== 0) return d
      const sc = s(a.subcategory).localeCompare(s(b.subcategory))
      if (sc !== 0) return sc
      return s(a.title).localeCompare(s(b.title))
    })
  }, [products])
  const countByDeal = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of sorted) {
      const k = p.deal_uuid || 'No Deal'
      map.set(k, (map.get(k) || 0) + 1)
    }
    return map
  }, [sorted])
  const handleClear = () => {
    onChange(null)
    setOpen(true) // keep menu open after clearing
  }
  const productTextColor = useMemo(() => {
    if (!value) return theme.palette.text.primary
    return value?.channel === 'Creative'
      ? theme.palette.icon.creative
      : value?.channel === 'Website'
      ? theme.palette.icon.website
      : theme.palette.icon.nco
  }, [value])
  return (
    <Grid
      item
      xs={open ? xsExpanded : xsCollapsed}
      sx={t => ({
        transition: t.transitions.create(['flex-basis', 'max-width'], {
          duration: 200,
          easing: t.transitions.easing.easeInOut,
        }),
      })}
    >
      <Box sx={{ position: 'relative', width: '100%', zIndex: open ? 5 : 'auto' }}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Autocomplete
            // control open state so width reacts to it
            open={open}
            onOpen={() => setOpen(true)}
            onClose={(_, reason) => {
              setOpen(false)
            }}
            disablePortal={false}
            fullWidth
            value={value}
            options={sorted}
            groupBy={opt => opt.deal_uuid || 'No Deal'}
            getOptionLabel={opt => opt.title || ''}
            onChange={(_, newVal) => onChange(newVal)}
            isOptionEqualToValue={(opt, v) => (opt?.uuid ?? opt?.id ?? opt?.SK) === (v?.uuid ?? v?.id ?? v?.SK)}
            // style internals (v5 API)
            PopperComponent={TypePopperWide as any}
            componentsProps={{
              popper: {
                widthStrategy: 'match-anchor',
                maxWidth: '90vw',
                zIndex: 2700,
                offset: [0, 2],
              } as any,
              // make the built-in clear "X" clear ids and keep menu open
              clearIndicator: {
                onMouseDown: (e: React.MouseEvent) => {
                  e.preventDefault()
                  e.stopPropagation()
                },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClear()
                },
                title: 'Clear assignees',
              },
            }}
            ListboxProps={{ style: { padding: '0px' } }}
            renderGroup={({ key, group, children }) => {
              const { dealStatus, name } = extractDealInfo(deals, group) ?? {}
              return (
                <li key={key} className="ProdGroup" style={{ padding: '20px !important' }}>
                  <Box
                    className="ProdGroupLabel"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      // backgroundColor: theme.palette.background.timelineGrid,
                      border: `1px solid ${theme.palette.icon.deals}`,
                      backdropFilter: 'blur(10px)',
                      width: '100%',
                    }}
                  >
                    <AttachMoneyIcon fontSize="small" sx={{ color: 'icon.deals' }} />
                    <Typography variant="caption" fontWeight={600} component="span" sx={{ textAlign: 'left' }}>
                      {name ?? 'No Name'}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      component="span"
                      sx={{ textAlign: 'flex-end', color: renderColorByString(dealStatus ?? null) }}
                    >
                      {dealStatus ?? 'No Status'}
                    </Typography>
                  </Box>
                  <ul className="ProdGroupUl">{children}</ul>
                </li>
              )
            }}
            renderOption={(props, option) => (
              <div key={option?.SK}>
                <ProductItemList props={props} option={option} />
              </div>
            )}
            renderInput={params => (
              <RenderInput
                params={params}
                displayLabel={label}
                placeholder={placeholder}
                setOpen={setOpen}
                denseHeight
                colors={{
                  color: disabled ? theme.palette.text.secondary : productTextColor,
                  labelColor: disabled ? theme.palette.text.secondary : theme.palette.text.primary,
                  WebkitTextFillColor: disabled ? theme.palette.text.secondary : productTextColor,
                }}
              />
            )}
            disabled={disabled}
            sx={{
              ...sx,
              '.MuiOutlinedInput-root': {
                padding: '0px 0px 0px 8px !important',
              },
            }}
          />
        )}
      </Box>
    </Grid>
  )
}

export default TaskProductAutocomplete
